import client from './client';
import { blobErrorMessage, getDownloadFilename } from './modifiedDuty';

/**
 * LA Sick (NPM payroll) — read-only: who did payroll-approved work on a Los Angeles job in a
 * calendar year, and whether Vista has them on CA LA SICK (app time-off policy 4 / 5).
 * Mirrors the API's GET /admin/timeOff/laSick?year= and POST /admin/timeOff/laSick/export
 * (API src/db/timeOff/laSickUtil.ts). Both 404 off-NPM and 403 without `viewLASick`.
 */

export type LaSickStatus = 'HAS_LA_SICK' | 'NEEDS_LA_SICK' | 'NEEDED_INACTIVE' | 'LA_SICK_NO_WORK';
export type LaSickExportSuffix = 'NeedsLASick' | 'HasLASick' | 'LASickNoLAWork';

export interface LaSickWeekRef {
    payPeriodID: number;
    /** PayPeriod.WeekNumber — the app's week number within `accountingYear`. */
    weekNumber: number;
    /** PayPeriod.AccountingYear. On NPM W1 of 2026 starts 2025-12-25 (Thursday). */
    accountingYear: number;
    /** YYYY-MM-DD — the pay period start (a Thursday on NPM). */
    startDate: string;
    /** YYYY-MM-DD */
    endDate: string;
}

/** null = hourly punches; "PIECE" = EmployeeTask claims; "RED" = MiscEmployeeTask claims. */
export type LaSickClaimType = 'PIECE' | 'RED' | null;

export interface LaSickWeekLine {
    projectID: number;
    claimType: LaSickClaimType;
    /** Hourly lines only (0 on PIECE / RED lines). */
    hours: number;
    /** PIECE / RED lines only (0 on hourly lines). */
    pieceAmount: number;
}

export interface LaSickWeek extends LaSickWeekRef {
    lines: LaSickWeekLine[];
}

export interface LaSickJob {
    projectID: number;
    altProjectID: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    hours: number;
    pieceAmount: number;
    weekCount: number;
}

export interface LaSickPolicy {
    timeOffPolicyID: number;
    name: string;
    /** Vista leave codes from the last sync; [] when not recorded. */
    leaveCodes: string[];
    /** true = policy 4 / 5 (CA LA Sick / CA LA Sick + Vacation). */
    isLaSick: boolean;
    /** Assigned by hand in the AdminApp, not by the sync. */
    assignedByAdmin: boolean;
    /** Inactive employees only: best "last sync while active" date (YYYY-MM-DD); may be null. */
    asOf: string | null;
}

export interface LaSickRow {
    employeeID: number;
    altEmployeeID: string;
    firstName: string;
    lastName: string;
    active: boolean;
    /** YYYY-MM-DD, inactive only; currently always null on NPM. */
    terminatedOn: string | null;
    /** null for past years. */
    status: LaSickStatus | null;
    isNew: boolean;
    laHours: number;
    laPieceAmount: number;
    laWeekCount: number;
    firstWeek: LaSickWeekRef | null;
    lastWeek: LaSickWeekRef | null;
    /** Hours desc, then piece $ desc, then job number. */
    jobs: LaSickJob[];
    /** The employee's open time-off policy (any policy; `isLaSick` marks 4 / 5), or null. */
    policy: LaSickPolicy | null;
    /** Week (pay period) order. */
    weeks: LaSickWeek[];
}

export interface LaSickTotals {
    /** ACTIVE employees with LA work in the year. */
    employeesWithLaWork: number;
    needs: number | null;
    has: number | null;
    noWork: number | null;
    neededInactive: number | null;
    /** In-year LA hours of ACTIVE employees. */
    laHours: number;
}

export interface LaSickResponse {
    year: number;
    isCurrentYear: boolean;
    /** Latest completed pay period holding a PayrollApproved timesheet. */
    throughWeek: LaSickWeekRef | null;
    /** ISO time of the last successful EMPLOYEES sync (Vista leave codes -> policies). */
    policiesSyncedAt: string | null;
    yearWeeks: { firstWeek: LaSickWeekRef; lastWeek: LaSickWeekRef } | null;
    totals: LaSickTotals;
    rows: LaSickRow[];
}

export function laSickErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { status?: number; data?: { error?: string; message?: string } } }).response;
        if (response?.status === 403) return 'Your role does not have the View LA Sick permission.';
        if (response?.data?.error) return response.data.error;
        if (response?.data?.message) return response.data.message;
    }
    if (err instanceof Error) return err.message;
    return "Couldn't load LA Sick for this year.";
}

export async function getLaSick(year: number): Promise<LaSickResponse> {
    const response = await client.get<LaSickResponse>('/admin/timeOff/laSick', { params: { year } });
    return response.data;
}

/**
 * Downloads the LA Sick .xlsx (Employees + Weekly detail sheets) for exactly the rows on
 * screen. `employeeIDs` must be in display (filtered + sorted) order; `suffix` names the quick
 * filter in the file name. `fallbackName` is used when the response has no Content-Disposition.
 */
export async function exportLaSick(
    body: { year: number; employeeIDs: number[]; suffix?: LaSickExportSuffix },
    fallbackName: string,
): Promise<{ blob: Blob; fileName: string; employeeRows: number; detailRows: number }> {
    try {
        const response = await client.post('/admin/timeOff/laSick/export', body, { responseType: 'blob' });
        const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
        const fileName = getDownloadFilename(contentDisposition, fallbackName);
        // Row counts of what the server actually wrote (it drops IDs that left the population since load).
        const employeeRows = Number(response.headers?.['x-lasick-employee-rows'] ?? body.employeeIDs.length);
        const detailRows = Number(response.headers?.['x-lasick-detail-rows'] ?? 0);
        return { blob: response.data as Blob, fileName, employeeRows, detailRows };
    } catch (error: any) {
        console.error('Error exporting LA Sick:', error);
        throw new Error(await blobErrorMessage(error, 'Failed to export LA Sick'));
    }
}
